import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Inbox, KeyRound, LogOut, Menu, X, Briefcase, Users, Star, Building2, Settings as Cog, Layers, Languages, FileEdit, BadgePercent } from "lucide-react";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import RocketIcon from "../../components/RocketIcon";

const NAV = [
  { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/live-editor", icon: LayoutDashboard, label: "🖥️ Canlı Düzenleyici" },
  { to: "/admin/blog", icon: FileText, label: "Blog" },
  { to: "/admin/works", icon: Briefcase, label: "Projeler" },
  { to: "/admin/services", icon: Layers, label: "Hizmetler" },
  { to: "/admin/pricing", icon: BadgePercent, label: "Paket Fiyatları" },
  { to: "/admin/site-content", icon: FileEdit, label: "Sayfa İçerikleri" },
  { to: "/admin/translations", icon: Languages, label: "Çeviriler" },
  { to: "/admin/clients", icon: Building2, label: "Müşteriler" },
  { to: "/admin/team", icon: Users, label: "Ekip" },
  { to: "/admin/reviews", icon: Star, label: "Yorumlar" },
  { to: "/admin/leads", icon: Inbox, label: "Leads" },
  { to: "/admin/settings", icon: Cog, label: "Ayarlar" },
  { to: "/admin/change-password", icon: KeyRound, label: "Şifre" },
];

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const itemClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors
     ${isActive
        ? "bg-brand-pink text-cream shadow-[0_8px_22px_-12px_rgba(238,79,122,0.6)]"
        : "text-ink/70 hover:text-ink hover:bg-mist/60"}`;

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="admin-layout">
      {/* Topbar — mobile only */}
      <div className="lg:hidden flex items-center justify-between border-b border-mist bg-white/80 backdrop-blur px-4 py-3">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2">
          <RocketIcon size={22} id="adml" className="-rotate-[15deg]" />
          <span className="font-display font-extrabold lowercase text-ink">yastık medya</span>
          <span className="text-xs uppercase tracking-[0.2em] text-brand-pink-deep">admin</span>
        </Link>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-full p-2 hover:bg-mist"
          data-testid="admin-mobile-menu-btn"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <div className="lg:grid lg:grid-cols-[260px_1fr]">
        {/* Sidebar */}
        <aside
          className={`${open ? "block" : "hidden"} lg:block
                       lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto
                       border-r border-mist bg-white px-5 py-7`}
          data-testid="admin-sidebar"
        >
          <Link to="/admin/dashboard" className="hidden lg:inline-flex items-center gap-2 mb-10">
            <RocketIcon size={26} id="adml-d" className="-rotate-[15deg]" />
            <div className="leading-tight">
              <div className="font-display font-extrabold lowercase">yastık medya</div>
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-brand-pink-deep font-bold">admin</div>
            </div>
          </Link>

          <nav className="flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={itemClass}
                data-testid={`admin-nav-${n.to.split("/").pop()}`}
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-10 rounded-2xl border border-mist bg-mist/40 p-4">
            <p className="text-xs text-ink/60">Giriş yapan</p>
            <p className="mt-1 text-sm font-semibold text-ink truncate">{admin?.email}</p>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="mt-3 inline-flex items-center gap-2 text-sm text-ink/70 hover:text-brand-pink-deep transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Çıkış yap
            </button>
          </div>
        </aside>

        {/* Main panel */}
        <main className="px-5 sm:px-8 py-8 lg:py-10 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
